import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { API_BASE_URL } from '../config/api';
import { Tag, TrendingDown, Award, Package } from 'lucide-react';

interface PromotionRow {
  id: number;
  name: string;
  promotion_type: string;
  is_active: boolean;
  uses: number;
  total_discount: string;
  items_affected: number;
}

interface ProductRow {
  product_name: string;
  lines: number;
  total_discount: string;
}

interface AnalyticsData {
  totals: { total_discount: string; total_uses: number };
  promotions: PromotionRow[];
  top_products: ProductRow[];
}

interface Props {
  dateRange: { from: string; to: string };
}

const PromotionsAnalytics: React.FC<Props> = ({ dateRange }) => {
  const { t } = useTranslation(['analytics', 'common']);
  const { store } = useAuth();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  const TYPE_LABELS: Record<string, string> = {
    percentage:    t('analytics:promotions.typePercentage',  { defaultValue: 'Percentage' }),
    fixed_amount:  t('analytics:promotions.typeFixedAmount', { defaultValue: 'Fixed amount' }),
    buy_x_get_y:   t('analytics:promotions.typeBuyXGetY',    { defaultValue: 'Buy + Get' }),
    bundle_price:  t('analytics:promotions.typeBundle',      { defaultValue: 'Bundle' })
  };

  useEffect(() => {
    if (!store?.id) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (dateRange.from) params.set('from', dateRange.from);
    if (dateRange.to) params.set('to', dateRange.to);
    fetch(`${API_BASE_URL}/api/stores/${store.id}/promotions/analytics?${params}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .finally(() => setLoading(false));
  }, [store?.id, dateRange.from, dateRange.to]);

  if (loading) return <div className="text-center py-12 text-t3">{t('common:loading', { defaultValue: 'Loading...' })}</div>;
  if (!data) return <div className="text-center py-12 text-t3">{t('analytics:promotions.noData', { defaultValue: 'No data available' })}</div>;

  const totalDiscount = parseFloat(data.totals?.total_discount || '0');
  const totalUses = data.totals?.total_uses || 0;

  return (
    <div className="space-y-6">
      {/* Top stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-surface-2 dark:bg-surface-1 border border-line rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-t3 text-sm">{t('analytics:promotions.totalDiscount', { defaultValue: 'Total discount' })}</span>
            <TrendingDown className="w-5 h-5 text-amber-700 dark:text-amber-400" />
          </div>
          <div className="text-2xl font-bold text-amber-800 dark:text-amber-300">{totalDiscount.toFixed(2)}€</div>
          <div className="text-xs text-t4 mt-1">{t('analytics:promotions.inSelectedPeriod', { defaultValue: 'in selected period' })}</div>
        </div>
        <div className="bg-surface-2 dark:bg-surface-1 border border-line rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-t3 text-sm">{t('analytics:promotions.timesApplied', { defaultValue: 'Times applied' })}</span>
            <Award className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{totalUses}</div>
          <div className="text-xs text-t4 mt-1">{t('analytics:promotions.totalApplications', { defaultValue: 'total applications' })}</div>
        </div>
        <div className="bg-surface-2 dark:bg-surface-1 border border-line rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-t3 text-sm">{t('analytics:promotions.avgDiscountPerUse', { defaultValue: 'Avg. discount/use' })}</span>
            <Tag className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
            {totalUses > 0 ? (totalDiscount / totalUses).toFixed(2) : '0.00'}€
          </div>
          <div className="text-xs text-t4 mt-1">{t('analytics:promotions.perOrder', { defaultValue: 'per order' })}</div>
        </div>
      </div>

      {/* Per promotion */}
      <div className="bg-surface-2 dark:bg-surface-1 border border-line rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex items-center gap-2">
          <Tag className="w-5 h-5 text-amber-700 dark:text-amber-400" />
          <h3 className="text-lg font-semibold text-t1">{t('analytics:promotions.perPromotionStats', { defaultValue: 'Statistics per promotion' })}</h3>
        </div>
        {data.promotions.length === 0 ? (
          <div className="p-8 text-center text-t3 text-sm">{t('analytics:promotions.noPromotions', { defaultValue: 'No promotions' })}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-0/50 text-t3 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">{t('analytics:promotions.colName', { defaultValue: 'Name' })}</th>
                  <th className="text-left px-4 py-3">{t('analytics:promotions.colType', { defaultValue: 'Type' })}</th>
                  <th className="text-right px-4 py-3">{t('analytics:promotions.colUses', { defaultValue: 'Uses' })}</th>
                  <th className="text-right px-4 py-3">{t('analytics:promotions.colItemsAffected', { defaultValue: 'Items affected' })}</th>
                  <th className="text-right px-4 py-3">{t('analytics:promotions.colTotalDiscount', { defaultValue: 'Total discount' })}</th>
                  <th className="text-center px-4 py-3">{t('analytics:promotions.colStatus', { defaultValue: 'Status' })}</th>
                </tr>
              </thead>
              <tbody>
                {data.promotions.map(p => (
                  <tr key={p.id} className="border-t border-line hover:bg-surface-3 dark:hover:bg-surface-2/30">
                    <td className="px-4 py-3 text-t1 font-medium">{p.name}</td>
                    <td className="px-4 py-3 text-t2">{TYPE_LABELS[p.promotion_type] || p.promotion_type}</td>
                    <td className="px-4 py-3 text-right text-t2">{p.uses}</td>
                    <td className="px-4 py-3 text-right text-t2">{p.items_affected}</td>
                    <td className="px-4 py-3 text-right font-semibold text-amber-800 dark:text-amber-300">
                      {parseFloat(p.total_discount).toFixed(2)}€
                    </td>
                    <td className="px-4 py-3 text-center">
                      {p.is_active ? (
                        <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 rounded text-xs">{t('common:active', { defaultValue: 'Active' })}</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-surface-2 text-t3 rounded text-xs">{t('common:inactive', { defaultValue: 'Inactive' })}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top products */}
      <div className="bg-surface-2 dark:bg-surface-1 border border-line rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex items-center gap-2">
          <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-semibold text-t1">{t('analytics:promotions.topDiscountedProducts', { defaultValue: 'Most discounted products' })}</h3>
        </div>
        {data.top_products.length === 0 ? (
          <div className="p-8 text-center text-t3 text-sm">{t('analytics:promotions.noData', { defaultValue: 'No data available' })}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-0/50 text-t3 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">{t('analytics:promotions.colProduct', { defaultValue: 'Product' })}</th>
                  <th className="text-right px-4 py-3">{t('analytics:promotions.colLines', { defaultValue: 'Lines' })}</th>
                  <th className="text-right px-4 py-3">{t('analytics:promotions.colTotalDiscount', { defaultValue: 'Total discount' })}</th>
                </tr>
              </thead>
              <tbody>
                {data.top_products.map((p, i) => (
                  <tr key={i} className="border-t border-line hover:bg-surface-3 dark:hover:bg-surface-2/30">
                    <td className="px-4 py-3 text-t1">{p.product_name}</td>
                    <td className="px-4 py-3 text-right text-t2">{p.lines}</td>
                    <td className="px-4 py-3 text-right font-semibold text-amber-800 dark:text-amber-300">
                      {parseFloat(p.total_discount).toFixed(2)}€
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotionsAnalytics;
